import {
	ChangeDetectionStrategy,
	Component,
	Inject,
	OnInit,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BaseComponent } from '@core/base.component';
import { Logger } from '@core/logger';

export enum ResolveMismatchOption {
	NO_UPDATE = 'no-update',
	UPDATE = 'update',
}

enum DisplayMode {
	LAWYERS_ONLY = 'lawyers-only',
	CLERKS_ONLY = 'clerks-only',
	BOTH = 'both',
}

interface ResolveLawyerClerkMismatchData {
	mismatchedLawyers?: {
		leapLawyerName: string;
		closerLawyerName: string;
	};
	mismatchedClerks?: {
		leapClerkName: string;
		closerClerkName: string;
	};
}

interface VM {
	mismatchedLawyers: {
		leapLawyerName: string;
		closerLawyerName: string;
	} | null;
	mismatchedClerks: {
		leapClerkName: string;
		closerClerkName: string;
	} | null;
	displayMode: DisplayMode;
}

const DefaultVMValues: VM = {
	mismatchedLawyers: null,
	mismatchedClerks: null,
	displayMode: DisplayMode.BOTH,
};

@Component({
	selector: 'app-resolve-lawyer-clerk-mismatch',
	templateUrl: './resolve-lawyer-clerk-mismatch.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class ResolveLawyerClerkMismatchComponent
	extends BaseComponent<VM>
	implements OnInit
{
	public DisplayMode = DisplayMode;
	public ResolveMismatchOption = ResolveMismatchOption;

	constructor(
		public dialogRef: MatDialogRef<
			ResolveLawyerClerkMismatchComponent,
			{ resolveMismatchSelection: ResolveMismatchOption }
		>,
		@Inject(MAT_DIALOG_DATA) public data: ResolveLawyerClerkMismatchData,
	) {
		super(structuredClone(DefaultVMValues));
	}

	ngOnInit(): void {
		const leapLawyerName = this.data?.mismatchedLawyers?.leapLawyerName ?? '';
		const closerLawyerName =
			this.data?.mismatchedLawyers?.closerLawyerName ?? '';

		const leapClerkName = this.data?.mismatchedClerks?.leapClerkName ?? '';
		const closerClerkName = this.data?.mismatchedClerks?.closerClerkName ?? '';

		let displayMode = DisplayMode.BOTH;

		if (
			leapLawyerName &&
			closerLawyerName &&
			!leapClerkName &&
			!closerClerkName
		) {
			displayMode = DisplayMode.LAWYERS_ONLY;
		} else if (
			!leapLawyerName &&
			!closerLawyerName &&
			leapClerkName &&
			closerClerkName
		) {
			displayMode = DisplayMode.CLERKS_ONLY;
		} else if (
			!leapLawyerName &&
			!closerLawyerName &&
			!leapClerkName &&
			!closerClerkName
		) {
			Logger.error(
				"[ResolveLawyerClerkMismatchComponent] At least one complete set of lawyers names' or clerks' names should be provided",
			);
		}

		this.VM = {
			mismatchedLawyers:
				displayMode === DisplayMode.LAWYERS_ONLY ||
				displayMode === DisplayMode.BOTH
					? { leapLawyerName, closerLawyerName }
					: null,
			mismatchedClerks:
				displayMode === DisplayMode.CLERKS_ONLY ||
				displayMode === DisplayMode.BOTH
					? { leapClerkName, closerClerkName }
					: null,
			displayMode,
		};
	}

	closeDialog(resolveMismatchSelection: ResolveMismatchOption) {
		this.dialogRef.close({
			resolveMismatchSelection: resolveMismatchSelection,
		});
	}
}
